import { HeadedCard } from '@/components/HeadedCard';
import { MoneyText } from '@/components/MoneyText';
import { StatusChip } from '@/components/StatusChip';
import { Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';

interface PendingStripProps {
  account: Account;
}

/** Pending-at-the-bank strip (MM-Q-22). Display only; nothing is clickable. */
export function PendingStrip({ account }: PendingStripProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const pending = account.connector?.pending ?? [];
  if (pending.length === 0) return null;
  const currency = account.currency ?? account.operatingCurrency ?? '';

  return (
    <HeadedCard
      title={t('connections.pendingTitle')}
      actions={<StatusChip domain="bankPending" status="PENDING" />}
    >
      <Table size="small" aria-label={t('connections.pendingTitle')}>
        <TableBody>
          {pending.map((row, index) => (
            <TableRow key={`${row.postedOn}-${row.payee}-${row.amountMinor}-${index}`}>
              <TableCell>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {/^\d{4}-\d{2}-\d{2}$/.test(row.postedOn)
                    ? formatCivilDate(row.postedOn, locale)
                    : row.postedOn}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {row.payee}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <MoneyText minor={row.amountMinor} currency={currency} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </HeadedCard>
  );
}
